import { valibotResolver } from "@hookform/resolvers/valibot";
import { Warning } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import RenderIf from "common/components/RenderIf";
import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import { HTTPError } from "common/helpers/HTTP";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { AssignSeatsSchema, type AssignSeatsSchemaType } from "common/schemas";
import type { AppSumoPromotion, LTDPromotion, Workspace } from "common/types";

import WorkspaceSelect from "./WorkspaceSelect";

function SeatsCount({ text, count }: { text: string; count: number }) {
  return (
    <div className="mt-4 flex items-center justify-between text-black-700">
      <span>{text}</span>
      <span className="text-sm-bold">{count}</span>
    </div>
  );
}

interface WorkspacePromotionProps {
  promotion: AppSumoPromotion | LTDPromotion;
  onClose: () => void;
}

export default function Promotion({
  promotion,
  onClose,
}: WorkspacePromotionProps) {
  const isDesktop = useTwBreakpoint("lg");
  const currentWorkspace = useSelectedWorkspaceContext();
  const [selectedWorkspace, setSelectedWorkspace] =
    useState<Workspace>(currentWorkspace);

  const {
    setLifetimeLicenseCount,
    isSettingLifetimeLicenseCount,
    setLifetimeLicenseCountError,
  } = useLicenseMutations();

  const { type, unassigned_license_count: seatsRemaining } = promotion;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      seats: selectedWorkspace.lifetime_license_count,
    },
    resolver: valibotResolver(AssignSeatsSchema),
  });

  const canAssign =
    watch("seats") === 0 ||
    (!!seatsRemaining &&
      (type !== "appsumo" || promotion.unassigned_license_count > 0));

  function onAssign(data: AssignSeatsSchemaType) {
    setLifetimeLicenseCount({
      count: data.seats,
      source: type,
      workspaceId: selectedWorkspace.id,
      accountsToCancel: [],
    }).then(onClose);
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>Manage your lifetime seats</Title>
      {type === "appsumo" && (
        <Description className="mb-4">
          Workspaces remaining: {promotion.unassigned_workspaces}
        </Description>
      )}

      <FormField label="Workspace" htmlFor="workspace-select">
        <WorkspaceSelect
          selectedWorkspace={selectedWorkspace}
          setSelectedWorkspace={setSelectedWorkspace}
        />
      </FormField>

      <SeatsCount
        text="Lifetime seats on this workspace"
        count={selectedWorkspace.lifetime_license_count}
      />

      <FormField
        label="Seat count"
        htmlFor="licenses"
        className="mt-8"
        error={errors.seats?.message}
      >
        <Input
          name="licenses"
          type="number"
          {...register("seats", {
            valueAsNumber: true,
          })}
        />
      </FormField>

      <SeatsCount text="Total seats remaining" count={seatsRemaining} />

      <RenderIf condition={!canAssign}>
        <Alert className="mt-4" variant="warning">
          <span className="text-xs-bold">No seats available</span> You have used
          up all of your lifetime seats. You can upgrade your package if your
          promotion is ongoing, or re-assign seats
        </Alert>
      </RenderIf>

      {setLifetimeLicenseCountError instanceof HTTPError &&
        setLifetimeLicenseCountError.type === "InvalidSubscriptionStatus" && (
          <div className="flex items-center gap-0.5 text-xs text-yellow-500 mt-4">
            <Warning weight="fill" size={16} />
            You cannot apply lifetime seats to a workspace in trial. Choose a
            different workspace, or cancel your trial
          </div>
        )}

      <div className="mt-8 flex justify-between gap-x-2.5">
        <Button
          variant="secondary-black"
          className="lg:flex-1"
          disabled={isSettingLifetimeLicenseCount}
          onClick={onClose}
        >
          Close
        </Button>

        <Button
          onClick={handleSubmit(onAssign)}
          className="lg:flex-1"
          isLoading={isSettingLifetimeLicenseCount}
          disabled={!canAssign}
        >
          Assign
        </Button>
      </div>
    </>
  );
}
